import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Grid, TextField, IconButton, Button, MenuItem,
  Select, FormControl, InputLabel, Chip, Alert, Tooltip, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownIcon from '@mui/icons-material/ArrowDownward';
import {
  listSupplierPaymentMilestones,
  replaceSupplierPaymentMilestones,
} from '../../services/procurementService';

// Trigger-event labels rendered in the dropdown. Keep keys in sync with
// backend payment-terms-service.js TRIGGER_EVENTS / MILESTONE_TYPES.
// Short labels so the Type dropdown doesn't truncate in the narrow row.
const MILESTONE_TYPE_LABELS = {
  on_order:    'On PO',
  on_shipment: 'On shipment',
  on_delivery: 'On delivery',
  net_days:    'Net X days',
  custom:      'Custom',
};

// Default mapping: when operator picks a milestone_type, sensible default
// trigger_event so they don't have to remember the pairing.
const TYPE_DEFAULT_TRIGGER = {
  on_order:    'po_created',
  on_shipment: 'goods_shipped',
  on_delivery: 'goods_received',
  net_days:    'goods_received',
  custom:      '',
};

const EMPTY_MILESTONE = {
  milestone_sequence: 1,
  milestone_type: 'net_days',
  milestone_name: 'Net 30',
  payment_percentage: 100,
  fixed_amount: 0,
  days_offset: 30,
  trigger_event: 'goods_received',
  notes: '',
};

/**
 * Payment Milestones editor for a single supplier.
 *
 * Pulls existing milestones on mount, lets operator add/remove/reorder rows,
 * and replaces the full list on save. Save fires via the parent's
 * onSave-collected callback so the Supplier dialog Save button orchestrates
 * BOTH the supplier update AND the milestones update (one click).
 *
 * Props:
 *   supplierId         — number, the supplier being edited (null when creating)
 *   onDirtyChange      — (boolean) => void, parent uses to enable/disable Save
 *   onCollectSaver     — (saver: async () => void) => void, parent calls saver
 *                        as part of its Save handler. saver is null when no
 *                        changes — parent should skip the call.
 */
export default function SupplierPaymentMilestones({ supplierId, onDirtyChange, onCollectSaver }) {
  const [milestones, setMilestones] = useState([]);
  const [originalJSON, setOriginalJSON] = useState('[]');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supplierId) {
      setMilestones([]);
      setOriginalJSON('[]');
      return;
    }
    let cancelled = false;
    setLoading(true);
    listSupplierPaymentMilestones(supplierId)
      .then(r => {
        if (cancelled) return;
        const list = Array.isArray(r?.data) ? r.data : [];
        setMilestones(list);
        setOriginalJSON(JSON.stringify(list.map(stripIdAndAuditFields)));
      })
      .catch(e => !cancelled && setError(e?.response?.data?.error || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [supplierId]);

  // Strip server-managed fields when computing dirty / sending to backend —
  // backend assigns new ids, recomputes created_at, etc.
  function stripIdAndAuditFields(m) {
    const { id, created_by, created_at, updated_at, is_active, ...rest } = m;
    return rest;
  }

  const isDirty = useMemo(() => {
    return JSON.stringify(milestones.map(stripIdAndAuditFields)) !== originalJSON;
  }, [milestones, originalJSON]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Wire the saver back to parent. Returns null when nothing to save so the
  // parent can skip the network call entirely.
  useEffect(() => {
    if (!onCollectSaver) return;
    if (!isDirty || !supplierId) {
      onCollectSaver(null);
      return;
    }
    onCollectSaver(async () => {
      const sanitized = milestones.map((m, idx) => ({
        ...stripIdAndAuditFields(m),
        milestone_sequence: idx + 1,
      }));
      const r = await replaceSupplierPaymentMilestones(supplierId, sanitized);
      const list = Array.isArray(r?.milestones) ? r.milestones : [];
      setMilestones(list);
      setOriginalJSON(JSON.stringify(list.map(stripIdAndAuditFields)));
      return r;
    });
  }, [isDirty, supplierId, milestones, onCollectSaver]);

  const totalPercentage = useMemo(() =>
    milestones.reduce((sum, m) => sum + (Number(m.payment_percentage) || 0), 0),
  [milestones]);
  const totalFixed = useMemo(() =>
    milestones.reduce((sum, m) => sum + (Number(m.fixed_amount) || 0), 0),
  [milestones]);

  const updateMilestone = (idx, patch) => {
    setMilestones(prev => prev.map((m, i) => i === idx ? { ...m, ...patch } : m));
  };

  const addMilestone = () => {
    setMilestones(prev => [...prev, {
      ...EMPTY_MILESTONE,
      milestone_sequence: prev.length + 1,
      // Default the new milestone to fill the percentage gap so totals sum.
      payment_percentage: Math.max(0, 100 - prev.reduce((s, m) => s + Number(m.payment_percentage || 0), 0)),
      milestone_name: prev.length === 0 ? 'Net 30' : `Milestone ${prev.length + 1}`,
    }]);
  };

  const removeMilestone = (idx) => {
    setMilestones(prev => prev.filter((_, i) => i !== idx));
  };

  const moveMilestone = (idx, dir) => {
    setMilestones(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleTypeChange = (idx, newType) => {
    updateMilestone(idx, {
      milestone_type: newType,
      trigger_event: TYPE_DEFAULT_TRIGGER[newType] || '',
    });
  };

  if (!supplierId) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          Save the supplier first to configure payment milestones.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Payment Milestones
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Optional. Splits invoice payment into staged tranches (e.g. 30% deposit / 70% on delivery).
            Leave empty to use the simple Payment Terms field above.
          </Typography>
        </Box>
        <Tooltip title={milestones.length >= 10 ? 'Maximum 10 milestones' : ''}>
          <span>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addMilestone}
              disabled={milestones.length >= 10}
            >
              Add milestone
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {loading ? (
        <Typography variant="caption" color="text.secondary">Loading milestones…</Typography>
      ) : milestones.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No milestones configured — invoices will use the simple payment terms above.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {milestones.map((m, idx) => (
            <Box key={idx} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
              <Grid container spacing={1.5} alignItems="flex-start">
                <Grid item xs={12} sm="auto">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Chip label={`#${idx + 1}`} size="small" sx={{ mb: 0.5 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton size="small" disabled={idx === 0} onClick={() => moveMilestone(idx, -1)}>
                            <ArrowUpIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton size="small" disabled={idx === milestones.length - 1} onClick={() => moveMilestone(idx, 1)}>
                            <ArrowDownIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2.5}>
                  <TextField
                    fullWidth size="small" label="Label"
                    placeholder="e.g. Deposit, Net 30"
                    value={m.milestone_name || ''}
                    onChange={(e) => updateMilestone(idx, { milestone_name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={m.milestone_type || 'net_days'}
                      onChange={(e) => handleTypeChange(idx, e.target.value)}
                    >
                      {Object.entries(MILESTONE_TYPE_LABELS).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={1.25}>
                  <TextField
                    fullWidth size="small" label="%"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={m.payment_percentage ?? ''}
                    onChange={(e) => updateMilestone(idx, { payment_percentage: e.target.value })}
                    helperText="0 if fixed"
                    FormHelperTextProps={{ sx: { fontSize: 9, mx: 0.25, mt: 0.25, whiteSpace: 'nowrap' } }}
                  />
                </Grid>
                <Grid item xs={6} sm={1.5}>
                  <TextField
                    fullWidth size="small" label="Fixed amt"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={m.fixed_amount ?? ''}
                    onChange={(e) => updateMilestone(idx, { fixed_amount: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <TextField
                    fullWidth size="small" label="Days"
                    type="number"
                    value={m.days_offset ?? ''}
                    onChange={(e) => updateMilestone(idx, { days_offset: e.target.value })}
                    helperText="from trigger"
                    FormHelperTextProps={{ sx: { fontSize: 9, mx: 0.25, mt: 0.25, whiteSpace: 'nowrap' } }}
                  />
                </Grid>
                <Grid item xs={10} sm={2.25}>
                  {/* Trigger is a free-text / custom field (Rishabh 2026-06-19): type
                      anything, or pick a known event from the suggestions. Optional —
                      it only auto-schedules when it matches a known event; otherwise
                      it's just a label. Never required. */}
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={['po_created', 'goods_shipped', 'goods_received', 'invoice_approved']}
                    inputValue={m.trigger_event || ''}
                    onInputChange={(e, val) => updateMilestone(idx, { trigger_event: val || '' })}
                    renderInput={(params) => (
                      <TextField {...params} label="Trigger" placeholder="optional / custom" />
                    )}
                  />
                </Grid>
                <Grid item xs={2} sm="auto">
                  <Tooltip title="Remove milestone">
                    <IconButton size="small" color="error" onClick={() => removeMilestone(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 0.5 }}>
            <Typography variant="caption" sx={{
              fontWeight: 600,
              // ≥99% counts as valid (allows 33.33 × 3 = 99.99 fractional splits)
              color: totalPercentage >= 99 && totalPercentage <= 100.01 ? 'success.main'
                   : totalPercentage > 100.01 ? 'error.main'
                   : totalFixed > 0 ? 'text.secondary' : 'warning.main',
            }}>
              Total: {totalPercentage.toFixed(2)}%
              {totalFixed > 0 && <> + {totalFixed.toFixed(2)} fixed</>}
              {totalPercentage > 0 && totalPercentage < 99 && totalFixed === 0 && (
                <> — must sum to ~100%</>
              )}
              {totalPercentage > 100.01 && <> — exceeds 100%</>}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
